
import { useState, useEffect } from "react";
import { Round } from "@/components/Round";
import { GameHeader } from "@/components/GameHeader";
import { GameOver } from "@/components/GameOver";
import { DebugPanel } from "@/components/game/DebugPanel";
import { smoothScrollToBottom } from "@/utils/uiUtils";
import { WordPair } from "@/types/game";

interface GameActiveStateProps {
  isDaily: boolean;
  gameId: string | null;
  rounds: WordPair[];
  currentWord: string;
  setCurrentWord: (word: string) => void;
  isSubmitting: boolean;
  botIsThinking: boolean;
  botHasChosenWord: boolean;
  shouldAutoFocus: boolean;
  setShouldAutoFocus: (autoFocus: boolean) => void;
  userHasSubmitted: boolean;
  wordError: string;
  handleSubmitWord: () => void;
  handleClearWordError: () => void;
  gameOver: boolean;
  convergenceRound: number | null;
  user: any;
  onSignInToSave: () => void;
  gameStatus: string;
}

export const GameActiveState = ({
  isDaily,
  gameId,
  rounds,
  currentWord,
  setCurrentWord,
  isSubmitting,
  botIsThinking,
  botHasChosenWord,
  shouldAutoFocus,
  setShouldAutoFocus,
  userHasSubmitted,
  wordError,
  handleSubmitWord,
  handleClearWordError,
  gameOver,
  convergenceRound,
  user,
  onSignInToSave,
  gameStatus
}: GameActiveStateProps) => {
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const MAX_ROUNDS = 10;
  const currentRound = rounds.length + 1;
  const isLastChance = currentRound === MAX_ROUNDS;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        setShowDebugPanel(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const activeInput = document.querySelector('.Round input:not([disabled])');
      if (activeInput instanceof HTMLElement) {
        activeInput.focus({ preventScroll: true });
        
        const roundElement = document.querySelector('.Round:last-child');
        if (roundElement) {
          const rect = roundElement.getBoundingClientRect();
          if (rect.bottom > window.innerHeight) {
            smoothScrollToBottom();
          }
        }
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <GameHeader isDaily={isDaily} />
      
      <main className="container mx-auto flex flex-col items-center justify-start py-16 px-4 mt-14">
        <div className="w-full max-w-3xl animate-fadeIn">
          <div className="space-y-8">
            {rounds.map((round, index) => (
              <Round
                key={index}
                userWord={round.userWord}
                aiWord={round.aiWord}
                aiExplanation={round.aiExplanation}
                proximityScore={round.proximityScore}
                isRevealed={true}
                roundNumber={index + 1}
                isLastRound={index === rounds.length - 1}
                isLastChance={index + 1 === MAX_ROUNDS}
              />
            ))}

            {!gameOver && (
              <Round
                userWord={currentWord}
                aiWord={botIsThinking ? "thinking" : (botHasChosenWord ? "Word chosen" : "...")}
                isRevealed={false}
                roundNumber={rounds.length + 1}
                isLastRound={true}
                isCurrentRound={true}
                isLastChance={isLastChance}
                autoFocus={shouldAutoFocus}
                botIsThinking={botIsThinking}
                userHasSubmitted={userHasSubmitted}
                wordError={wordError}
                onWordChange={(word) => {
                  if (wordError) {
                    handleClearWordError();
                  }
                  
                  setCurrentWord(word);
                  if (shouldAutoFocus) setShouldAutoFocus(false);
                }}
                onSubmit={handleSubmitWord}
                onClearWordError={handleClearWordError}
                isSubmitting={isSubmitting}
              />
            )}

            {gameOver && (
              <GameOver 
                convergenceRound={convergenceRound}
                roundsLength={rounds.length}
                user={user}
                onSignInToSave={onSignInToSave}
                gameStatus={gameStatus}
                rounds={rounds}
              />
            )}
            
            {showDebugPanel && (
              <DebugPanel
                gameId={gameId}
                currentRound={currentRound}
                botIsThinking={botIsThinking}
                botHasChosenWord={botHasChosenWord}
                userHasSubmitted={userHasSubmitted}
                isSubmitting={isSubmitting}
                rounds={rounds}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
